import { useState } from 'react';
import { Check, Eye, EyeOff, X } from 'lucide-react';
import LoginAndSecurity from '@/assets/icons/settings/login-and-security.svg';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { getApiErrorMessage } from '@/api/apiErrors';
import { changePasswordRequest } from '@/api/apiAuth';
import { useAuth } from '@/providers/useAuth';



import '@/pages/settings/SettingsPage.scss';

export function SettingsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [editingPassword, setEditingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [isSubmittingPassword, setIsSubmittingPassword] = useState(false);

  const displayEmail = user?.email ?? t('settings.loginSecurity.emailUnavailable');
  const showPasswordChecklist = newPassword.trim().length > 0;

  const passwordChecks = [
    {
      id: 'minLength',
      label: t('settings.loginSecurity.passwordRuleMinLength'),
      met: newPassword.length >= 8,
    },
    {
      id: 'number',
      label: t('settings.loginSecurity.passwordRuleNumber'),
      met: /\d/.test(newPassword),
    },
    {
      id: 'uppercase',
      label: t('settings.loginSecurity.passwordRuleUppercase'),
      met: /[A-Z]/.test(newPassword),
    },
    {
      id: 'different',
      label: t('settings.loginSecurity.passwordRuleDifferent'),
      met: currentPassword.length > 0 && newPassword !== currentPassword,
    },
  ];

  const handleEmailChange = () => {
    toast.info(t('settings.loginSecurity.emailChangePending'));
  };

  const handlePasswordChangeClick = () => {
    setEditingPassword(true);
    setCurrentPassword('');
    setNewPassword('');
    setShowCurrentPassword(false);
    setShowNewPassword(false);
  };

  const handlePasswordCancel = () => {
    setEditingPassword(false);
    setCurrentPassword('');
    setNewPassword('');
    setShowCurrentPassword(false);
    setShowNewPassword(false);
  };

  const handlePasswordConfirm = async () => {
    const trimmedCurrentPassword = currentPassword.trim();
    const trimmedNewPassword = newPassword.trim();

    if (!trimmedCurrentPassword || !trimmedNewPassword) {
      toast.error(t('settings.loginSecurity.passwordRequired'));
      return;
    }

    setIsSubmittingPassword(true);
    try {
      await changePasswordRequest({
        current_password: trimmedCurrentPassword,
        new_password: trimmedNewPassword,
      });
      toast.success(t('settings.loginSecurity.passwordChanged'));
      handlePasswordCancel();
    } catch (error) {
      toast.error(getApiErrorMessage(error, t('settings.loginSecurity.passwordChangeError')));
    } finally {
      setIsSubmittingPassword(false);
    }
  };

  return (
    <div className="page-stack settings-page">
      <section className="settings-page__header">
        <h1>{t('settings.title')}</h1>
      </section>

      <article className="surface-card settings-security-card">
        <div className="settings-security-card__title">
          <img src={LoginAndSecurity} alt="" aria-hidden="true" />
          <span>{t('settings.loginSecurity.title')}</span>
        </div>

        <div className="settings-security-card__rows">
          <div className="settings-security-card__row">
            <div className="settings-security-card__label-wrap">
              <span className="settings-security-card__label">
                {t('settings.loginSecurity.emailLabel')}
              </span>
            </div>

            <div className="settings-security-card__value-wrap">
              <span>{displayEmail}</span>
              <Check 
                size={14} 
                className="settings-security-card__verified-icon" 
                aria-hidden="true" 
              />
            </div>

            <button 
              type="button" 
              className={`settings-link-button disabled`}
              onClick={handleEmailChange}
              disabled
            >
              {t('settings.actions.change')}
            </button>
          </div>

          <div className="settings-security-card__row settings-security-card__row--password">
            <div className="settings-security-card__label-wrap">
              <span className="settings-security-card__label">
                {t('settings.loginSecurity.passwordLabel')}
              </span>
            </div>

            {!editingPassword ? (
              <>
                <div className="settings-security-card__value-wrap">
                  <span aria-label={t('settings.loginSecurity.hiddenPasswordAria')}>
                    ••••••••
                  </span>
                </div>

                <button 
                  type="button" 
                  className="settings-link-button" 
                  onClick={handlePasswordChangeClick}
                >
                  {t('settings.actions.change')}
                </button>
              </>
            ) : (
              <div className="settings-password-form">
                <label className="field">
                  <span className="settings-security-card__label">
                    {t('settings.loginSecurity.currentPasswordLabel')}
                  </span>
                  <div className="settings-password-form__control-wrap">
                    <input
                      className="field__control"
                      type={showCurrentPassword ? 'text' : 'password'}
                      value={currentPassword}
                      onChange={(event) => setCurrentPassword(event.target.value)}
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      className="settings-password-form__toggle"
                      onClick={() => setShowCurrentPassword((value) => !value)}
                      aria-label={
                        showCurrentPassword
                          ? t('settings.loginSecurity.hidePassword')
                          : t('settings.loginSecurity.showPassword')
                      }
                    >
                      {showCurrentPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                </label>

                <label className="field">
                  <span className="settings-security-card__label">
                    {t('settings.loginSecurity.newPasswordLabel')}</span>
                  <div className="settings-password-form__control-wrap">
                    <input
                      className="field__control"
                      type={showNewPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(event) => setNewPassword(event.target.value)}
                      title={newPassword ? t('settings.loginSecurity.passwordRulesHint') : undefined}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="settings-password-form__toggle"
                      onClick={() => setShowNewPassword((value) => !value)}
                      aria-label={
                        showNewPassword
                          ? t('settings.loginSecurity.hidePassword')
                          : t('settings.loginSecurity.showPassword')
                      }
                    >
                      {showNewPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>

                  {showPasswordChecklist ? (
                    <div className="settings-password-rules" role="status" aria-live="polite">
                      <p className="settings-password-rules__title">
                        {t('settings.loginSecurity.passwordChecklistTitle')}
                      </p>
                      <ul className="settings-password-rules__list">
                        {passwordChecks.map((rule) => (
                          <li
                            key={rule.id}
                            className={`settings-password-rules__item ${
                              rule.met
                                ? 'settings-password-rules__item--valid'
                                : 'settings-password-rules__item--invalid'
                            }`}
                          >
                            {rule.met ? <Check size={14} aria-hidden="true" /> : <X size={14} aria-hidden="true" />}
                            <span>{rule.label}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </label>

                <div className="settings-password-form__actions">
                  <button 
                    type="button" 
                    className="button button--primary" 
                    onClick={handlePasswordConfirm}
                    disabled={isSubmittingPassword || !passwordChecks.every((check) => check.met)}
                  >
                    {isSubmittingPassword ? t('settings.loginSecurity.passwordChanging') : t('settings.actions.confirm')}
                  </button>
                  <button 
                    type="button" 
                    className="button button--secondary" 
                    onClick={handlePasswordCancel}
                  >
                    {t('settings.actions.cancel')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </article>
    </div>
  );
}
